/**
 * Die eine Wahrheit darüber, wie ein rohes Mangel-Dokument anzuzeigen ist.
 * Bewusst ohne Firestore- und ohne React-Bezug: die Ableitungen aus dem
 * gewachsenen Datenmodell — Fahrzeugbezug, Status, Eingangsdatum, Priorität —
 * sind die eigentliche Fachlichkeit und hier ohne jeden Double prüfbar. Die
 * Komponenten rendern nur noch.
 *
 * Genutzt von der Mängelliste (`components/Maengel`) und von der Mangelkarte
 * auf der Fahrzeugseite (`components/MangelBox`) — derselbe Mangel soll an
 * beiden Orten dieselbe Beschriftung und dieselbe Farbe tragen.
 */
import moment from "moment";

/** Länge, ab der die Beschreibung in der Tabelle gekürzt wird. */
const BESCHREIBUNG_MAX_ZEICHEN = 120;

/**
 * Zeitangaben liegen im Bestand uneinheitlich vor: Firestore liefert
 * `{seconds, nanoseconds}`, ein Timestamp-Objekt kann `toDate()` mitbringen,
 * und in Tests steht auch mal ein echtes Date. Alles landet hier als `Date`
 * oder als `null` — ein unlesbarer Wert darf die Liste nicht sprengen.
 */
const alsDatum = (wert) => {
  if (!wert) {
    return null;
  }

  if (wert instanceof Date) {
    return isNaN(wert.getTime()) ? null : wert;
  }

  if (typeof wert.toDate === "function") {
    const datum = wert.toDate();
    return datum instanceof Date && !isNaN(datum.getTime()) ? datum : null;
  }

  if (typeof wert.seconds === "number") {
    return new Date(wert.seconds * 1000);
  }

  return null;
};

const ersterHistorieEintrag = (mangel) => {
  const historie = Array.isArray(mangel.historie) ? mangel.historie : [];
  return historie.length > 0 ? historie[0] : null;
};

/**
 * Das Eingangsdatum kommt aus dem ersten Historie-Eintrag: Historie-Einträge
 * werden nur angehängt, während das Anlege-Datumsfeld im Altbestand beim
 * Bearbeiten überschrieben wurde. Ein alter Mangel erschiene sonst als frisch.
 * Das Anlege-Datum bleibt die Rückfallebene für Dokumente ohne Historie.
 */
const eingangsDatum = (mangel) => {
  const ersterEintrag = ersterHistorieEintrag(mangel);
  return alsDatum(ersterEintrag?.datum) ?? alsDatum(mangel.datumAngelegt);
};

/**
 * Vier Zustände aus zwei Feldern: das „neu"-Kennzeichen sticht das Statusfeld,
 * solange niemand den Mangel angefasst hat. Danach entscheidet der Status —
 * unbekannte Werte gelten als offen, genau wie auf der Mangelkarte.
 */
const mangelStatus = (mangel) => {
  if (mangel.new) {
    return "neu";
  }

  switch (mangel.status) {
    case "erledigt":
      return "erledigt";
    case "backlog":
      return "backlog";
    default:
      return "offen";
  }
};

/**
 * Auch die Mangelkarte auf der Fahrzeugseite beschriftet ihre Status-Wechsel
 * hierüber: derselbe Zustand soll überall dasselbe Wort tragen.
 */
export const statusText = (status) => {
  switch (status) {
    case "neu":
      return "Neu";
    case "erledigt":
      return "Erledigt";
    case "backlog":
      return "Zurückgestellt";
    default:
      return "Offen";
  }
};

/**
 * Die Priorität liegt uneinheitlich vor — beim Anlegen als Zahl, beim
 * Bearbeiten als Zeichenkette. Hier wird sie einmal normalisiert, damit
 * derselbe Mangel nicht je nach Herkunft des Dokuments anders eingefärbt wird.
 */
const prioritaetStufe = (wert) => {
  switch (String(wert ?? "").trim()) {
    case "1":
      return "1";
    case "2":
      return "2";
    case "3":
      return "3";
    default:
      return "4";
  }
};

/**
 * Beschriftung und CSS-Klasse einer Priorität — die eine Wahrheit für Liste und
 * Mangelkarte. Ein erledigter Mangel wird neutral eingefärbt, behält aber seinen
 * Prioritätstext; sonst bedeutete dieselbe Farbe an zwei Orten Verschiedenes.
 */
export const prioritaetAnzeige = (wert, status) => {
  const stufe = prioritaetStufe(wert);

  const texte = {
    1: "Hohe Priorität",
    2: "Mittlere Priorität",
    3: "Niedriege Priorität",
    4: "Ohne Priorität",
  };

  return {
    stufe,
    text: texte[stufe],
    klasse: status === "erledigt" ? "prio4" : "prio" + stufe,
  };
};

const einzeilig = (text) => {
  const flach = String(text ?? "")
    .replace(/\s+/g, " ")
    .trim();

  if (flach.length <= BESCHREIBUNG_MAX_ZEICHEN) {
    return flach;
  }

  return flach.slice(0, BESCHREIBUNG_MAX_ZEICHEN).trimEnd() + "…";
};

const zeileAusMangel = (mangel, fahrzeug) => {
  const status = mangelStatus(mangel);
  const prioritaet = prioritaetAnzeige(mangel.priorität, status);
  const eingang = eingangsDatum(mangel);
  const melder = mangel.kontaktName ?? "";
  const volleBeschreibung = ersterHistorieEintrag(mangel)?.beschreibung;

  return {
    id: mangel.id,
    fahrzeugId: mangel.fahrzeugId,
    kennzeichen: fahrzeug.kennzeichen ?? "",
    eingang,
    eingangText: eingang
      ? moment(eingang).format("DD.MM.YYYY, HH:mm") + " Uhr"
      : "",
    kategorie: mangel.kategorie ?? "",
    beschreibung: einzeilig(volleBeschreibung),
    melder,
    // Die Suche vergleicht gegen den vollen Beschreibungstext, nicht gegen den
    // gekürzten Anriss der Tabelle: sonst fände sie einen Begriff nicht, nur
    // weil er hinter der Kürzungsgrenze steht.
    suchtext: [
      volleBeschreibung,
      fahrzeug.kennzeichen,
      mangel.kategorie,
      melder,
    ]
      .map((feld) => String(feld ?? ""))
      .join(" ")
      .toLowerCase(),
    status,
    statusText: statusText(status),
    prioritaetStufe: prioritaet.stufe,
    prioritaetText: prioritaet.text,
    prioritaetKlasse: prioritaet.klasse,
  };
};

/**
 * Nimmt die rohen Mangel-Dokumente samt Parent-ID (`fahrzeugId`) und die
 * Fahrzeugliste entgegen und gibt die fertigen Zeilen zurück, fest nach Eingang
 * absteigend sortiert.
 *
 * Mängel ohne existierendes Fahrzeug fallen heraus: Firestore löscht
 * Subcollections nicht mit dem Elterndokument, solche Mängel sind weder
 * anspringbar noch bearbeitbar und gehören darum nicht in die Arbeitsliste.
 */
export const maengelZeilen = (mangelDokumente, fahrzeuge) => {
  const fahrzeugNachId = new Map(
    (fahrzeuge ?? []).map((fahrzeug) => [fahrzeug.id, fahrzeug]),
  );

  const zeilen = (mangelDokumente ?? [])
    .filter((mangel) => fahrzeugNachId.has(mangel.fahrzeugId))
    .map((mangel) =>
      zeileAusMangel(mangel, fahrzeugNachId.get(mangel.fahrzeugId)),
    );

  // Ohne lesbares Eingangsdatum ans Ende: eine Zeile ohne Datum darf die
  // Reihenfolge der datierten nicht durcheinanderbringen.
  return zeilen.sort((links, rechts) => {
    if (!links.eingang && !rechts.eingang) {
      return 0;
    }
    if (!links.eingang) {
      return 1;
    }
    if (!rechts.eingang) {
      return -1;
    }

    return rechts.eingang.getTime() - links.eingang.getTime();
  });
};

/**
 * Wie viele Meldungen neu eingegangen sind — die Zahl am Menüpunkt „Mängel".
 *
 * Gezählt wird über `maengelZeilen` und damit über genau denselben Bestand, den
 * die Liste zeigt: Zähler und Liste könnten sich sonst widersprechen, und
 * verwaiste Mängel — Fahrzeug gelöscht, Mangel nicht mehr erreichbar — würden
 * den Zähler dauerhaft von null fernhalten.
 *
 * Bewusst ohne Filter-Argument: der Zähler misst den Eingang, nicht den gerade
 * eingestellten Ausschnitt der Tabelle.
 */
export const neueMaengelAnzahl = (mangelDokumente, fahrzeuge) => {
  return maengelZeilen(mangelDokumente, fahrzeuge).filter(
    (zeile) => zeile.status === "neu",
  ).length;
};

/**
 * Die Auswahl des Statusfilters. „Offene" ist eine Sammelauswahl über die drei
 * Zustände, an denen noch zu arbeiten ist — sie ist die Vorbelegung, damit die
 * Liste als Arbeitsliste aufgeht. Erledigte sind darüber jederzeit wieder
 * erreichbar; ausgeblendet heißt hier nie verloren.
 */
export const statusFilterOptionen = [
  {wert: "offene", text: "Offene"},
  {wert: "neu", text: statusText("neu")},
  {wert: "offen", text: statusText("offen")},
  {wert: "backlog", text: statusText("backlog")},
  {wert: "erledigt", text: statusText("erledigt")},
  {wert: "alle", text: "Alle"},
];

const OFFENE_STATUS = ["neu", "offen", "backlog"];

/**
 * Die Vorbelegung der Filterzeile — zugleich das, was die Rücksetz-Schaltfläche
 * wiederherstellt. Als Funktion, damit niemand versehentlich den geteilten
 * Ausgangszustand überschreibt.
 */
export const maengelFilterVorbelegung = () => ({
  status: "offene",
  fahrzeugId: "",
  prioritaet: "",
});

const passtZumStatus = (zeile, status) => {
  if (!status || status === "alle") {
    return true;
  }

  if (status === "offene") {
    return OFFENE_STATUS.includes(zeile.status);
  }

  return zeile.status === status;
};

/**
 * Filtert die fertigen Zeilen im Browser auf dem bereits geladenen Bestand.
 * Die drei Filter wirken einzeln und kombiniert; ein leerer Wert schränkt nicht
 * ein. Die Reihenfolge aus `maengelZeilen` bleibt erhalten.
 *
 * Verglichen wird gegen die schon normalisierten Felder der Zeile — darum ist
 * dem Prioritätsfilter einerlei, ob im Dokument eine Zahl oder eine Zeichenkette
 * stand.
 */
export const maengelFiltern = (zeilen, filter) => {
  const {status, fahrzeugId, prioritaet} = filter ?? {};

  return (zeilen ?? []).filter((zeile) => {
    if (!passtZumStatus(zeile, status)) {
      return false;
    }

    if (fahrzeugId && zeile.fahrzeugId !== fahrzeugId) {
      return false;
    }

    if (prioritaet && zeile.prioritaetStufe !== prioritaet) {
      return false;
    }

    return true;
  });
};

/**
 * Die Freitextsuche der Kopfleiste, angewandt auf die bereits gefilterte Liste
 * — Suche und Filter grenzen dadurch gemeinsam ein.
 *
 * Gesucht wird über Beschreibung, Fahrzeug, Kategorie und Melder zugleich: wer
 * sich nur vage an einen Mangel erinnert, muss nicht wissen, in welchem Feld
 * der Begriff steht. Groß- und Kleinschreibung spielen keine Rolle, denn der
 * Suchtext der Zeile liegt schon kleingeschrieben vor.
 */
export const maengelSuchen = (zeilen, suchbegriff) => {
  const begriff = String(suchbegriff ?? "")
    .trim()
    .toLowerCase();

  if (begriff === "") {
    return zeilen ?? [];
  }

  return (zeilen ?? []).filter((zeile) => zeile.suchtext.includes(begriff));
};

/** Zeilen pro Seite der Mängelliste. */
export const MAENGEL_SEITENGROESSE = 50;

/**
 * Schneidet die bereits gefilterte und durchsuchte Liste in Seiten. Es wird im
 * Browser geschnitten, weil der Bestand ohnehin vollständig geladen ist — der
 * Fuß soll den ganzen Arbeitsvorrat beziffern, nicht nur das gerade Geladene.
 *
 * Eine Seite außerhalb des Bestands wird an den Rand zurückgeholt statt leer
 * auszugeben: eine schrumpfende Liste — ein Filterwechsel, ein erledigter
 * Mangel — darf den Nutzer nicht vor einer leeren Seite stehen lassen. Die
 * Seitenzahl bleibt darum mindestens 1, auch bei leerer Liste.
 */
export const maengelSeite = (zeilen, seite) => {
  const bestand = zeilen ?? [];
  const seitenAnzahl = Math.max(
    1,
    Math.ceil(bestand.length / MAENGEL_SEITENGROESSE),
  );
  const gewaehlt = Math.min(Math.max(Math.trunc(seite) || 1, 1), seitenAnzahl);
  const start = (gewaehlt - 1) * MAENGEL_SEITENGROESSE;

  return {
    zeilen: bestand.slice(start, start + MAENGEL_SEITENGROESSE),
    seite: gewaehlt,
    seitenAnzahl,
    gesamt: bestand.length,
  };
};

/**
 * Die Fahrzeuge der Auswahlliste kommen aus dem angezeigten Bestand, nicht aus
 * dem Fahrzeug-State: Ein Fahrzeug ohne Mangel als Auswahl anzubieten hieße,
 * eine garantiert leere Trefferliste anzubieten.
 */
export const fahrzeugFilterOptionen = (zeilen) => {
  const nachId = new Map();

  (zeilen ?? []).forEach((zeile) => {
    if (zeile.fahrzeugId && !nachId.has(zeile.fahrzeugId)) {
      nachId.set(zeile.fahrzeugId, {
        wert: zeile.fahrzeugId,
        text: zeile.kennzeichen,
      });
    }
  });

  return Array.from(nachId.values()).sort((links, rechts) =>
    links.text.localeCompare(rechts.text, "de"),
  );
};

/**
 * Die beiden Stolpersteine dieser Seite liegen außerhalb des Repositories:
 * Security Rule und Collection-Group-Index pflegt der Betreiber von Hand
 * (siehe docs/firebase-konsole.md). Fehlt eines von beidem, soll der Nutzer
 * lesen, was zu tun ist, statt vor einer leeren Seite zu stehen — darum ist die
 * Zuordnung von Fehlercode zu Satz hier prüfbar und nicht im Listener versteckt.
 */
export const ladefehlerText = (fehler) => {
  switch (fehler?.code) {
    case "failed-precondition":
      return "Für die Mängelliste fehlt ein Firestore-Index. Bitte Index anlegen und Seite neu laden.";
    case "permission-denied":
      return "Keine Leseberechtigung für die Mängelliste. Bitte die Firestore-Regeln prüfen.";
    default:
      return "Mängel konnten nicht geladen werden.";
  }
};
